// Coworker schedules: recurring headless threads. CRUD + run-now, backed by
// /json/task-schedules (see backend routes/taskRoutes.js).
import React, { useCallback, useEffect, useState } from 'react';
import {
  View, ScrollView, StyleSheet, TextInput, RefreshControl, Pressable, Platform, Switch, Modal,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import { useAuthStore, useToastStore, useCoworkerStore } from '@/store';
import { openHomeComposer } from '@/lib/openHomeComposer';
import useModalHelper from '@/hooks/useModalHelper';
import { describeWorkSchedule, WORK_SCHEDULE_DAYS as DAYS } from '@/constants/workSchedules';
import { useShallow } from 'zustand/react/shallow';
import moment from 'moment-mini';

const TIMINGS = [
  { label: 'Immediately', value: 'immediate' },
  { label: 'Morning (7am)', value: 'morning' },
  { label: 'Afternoon (12pm)', value: 'afternoon' },
  { label: 'Evening (6pm)', value: 'evening' },
];

function WebNativeInput({ type, value, onChange, label, colors, border }) {
  return (
    // Native date/time pickers — RN TextInput does not forward type=datetime-local.
    // eslint-disable-next-line react/forbid-elements
    <input
      type={type}
      value={value || ''}
      aria-label={label}
      onChange={(event) => onChange(event.target.value || '')}
      style={{
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: border,
        borderRadius: 10,
        minHeight: 42,
        padding: '10px 12px',
        fontSize: 14,
        width: '100%',
        boxSizing: 'border-box',
        outline: 'none',
        backgroundColor: 'transparent',
        color: colors.text_color_primary,
      }}
    />
  );
}

function ScheduleRow({ schedule, colors, border, onEdit, onRun, onDelete, onToggle, isBusy }) {
  const title = schedule.name?.trim() || 'Untitled schedule';
  const totalRuns = Math.max(0, Number(schedule.stats?.totalRuns) || 0);
  const runSummary = totalRuns
    ? `${totalRuns} ${totalRuns === 1 ? 'run' : 'runs'}${schedule.stats?.lastRunAt ? ` · Last ran ${moment(schedule.stats.lastRunAt).format('MMM D')}` : ''}`
    : 'Not run yet';

  return (
    <View style={{ ...styles.card, backgroundColor: colors.solid_color_primary, borderColor: border }}>
      <View style={styles.cardHeader}>
        <Text size="n" weight="semibold" numberOfLines={1} style={{ flex: 1, marginRight: 8 }}>
          {title}
        </Text>
        <Switch
          value={!!schedule.isEnabled}
          onValueChange={(value) => onToggle(schedule, value)}
          disabled={isBusy}
          accessibilityLabel={`${schedule.isEnabled ? 'Pause' : 'Activate'} ${title}`}
          accessibilityHint="Controls whether this schedule runs automatically"
          accessibilityState={{ checked: !!schedule.isEnabled, disabled: isBusy }}
        />
      </View>
      <View style={styles.scheduleSummary}>
        <View style={[styles.statusDot, { backgroundColor: schedule.isEnabled ? colors.color_success : colors.text_color_tertiary }]} />
        <Text size="xxs" weight="semibold" color={schedule.isEnabled ? undefined : 'tertiary'}>
          {schedule.isEnabled ? 'Active' : 'Paused'}
        </Text>
        <Text size="xxs" color="tertiary">·</Text>
        <Text size="xxs" color="secondary" numberOfLines={1} style={styles.scheduleCopy}>
          {describeWorkSchedule(schedule)}
        </Text>
      </View>
      <Text size="s" color="secondary" numberOfLines={2} style={styles.schedulePrompt}>{schedule.prompt}</Text>
      <View style={styles.scheduleMeta}>
        <Icon name="clock" size={11} color={colors.text_color_tertiary} />
        <Text size="xxs" color="tertiary">{runSummary}</Text>
        {!!schedule.emailDelivery?.isEnabled && (
          <>
            <Text size="xxs" color="tertiary">·</Text>
            <Icon name="envelope" size={11} color={colors.text_color_tertiary} />
            <Text size="xxs" color="tertiary">Email delivery</Text>
          </>
        )}
      </View>
      <View style={[styles.cardDivider, { backgroundColor: border }]} />
      <View style={styles.cardActions}>
        <Button
          text="Edit"
          prependIcon="pen"
          size="small"
          variant="secondary"
          onPress={() => onEdit(schedule)}
          isDisabled={isBusy}
          role="button"
          accessibilityRole="button"
          accessibilityLabel={`Edit ${title}`}
          style={styles.cardAction}
        />
        <Button
          text="Run now"
          prependIcon="play"
          size="small"
          variant="secondary"
          iconColor={colors.color_success}
          onPress={() => onRun(schedule)}
          isDisabled={isBusy}
          role="button"
          accessibilityRole="button"
          accessibilityLabel={`Run ${title} now`}
          style={styles.cardAction}
        />
        <Button
          text="Delete"
          prependIcon="trash"
          size="small"
          variant="danger"
          isHollow
          onPress={() => onDelete(schedule)}
          isDisabled={isBusy}
          role="button"
          accessibilityRole="button"
          accessibilityLabel={`Delete ${title}`}
          style={styles.cardAction}
        />
      </View>
    </View>
  );
}

function ScheduleFormModal({ visible, onClose, onSubmit, initial, colors, border, isBusy, isMobile }) {
  const editing = !!initial?.id;
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [type, setType] = useState('daily');
  const [hour, setHour] = useState(8);
  const [minute, setMinute] = useState(0);
  const [daysOfWeek, setDaysOfWeek] = useState([1]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [runAt, setRunAt] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [emailTiming, setEmailTiming] = useState('immediate');

  useEffect(() => {
    if (!visible) return;
    setName(initial?.name || '');
    setPrompt(initial?.prompt || '');
    setType(initial?.schedule?.type || 'daily');
    setHour(initial?.schedule?.timeOfDay?.hour ?? 8);
    setMinute(initial?.schedule?.timeOfDay?.minute ?? 0);
    setDaysOfWeek(initial?.schedule?.daysOfWeek?.length ? initial.schedule.daysOfWeek : [1]);
    setDayOfMonth(initial?.schedule?.dayOfMonth || 1);
    setRunAt(initial?.schedule?.runAt ? moment(initial.schedule.runAt).format('YYYY-MM-DDTHH:mm') : '');
    setEmailEnabled(initial?.emailDelivery?.isEnabled !== false);
    setEmailTiming(initial?.emailDelivery?.timing || 'immediate');
  }, [visible, initial]);

  const submit = useCallback(() => {
    const schedule = { type, timezone: 'America/New_York', timeOfDay: { hour, minute } };
    if (type === 'once') schedule.runAt = runAt;
    if (type === 'weekly') schedule.daysOfWeek = daysOfWeek;
    if (type === 'monthly' || type === 'annually') schedule.dayOfMonth = dayOfMonth;
    onSubmit({
      name: name.trim(),
      prompt: prompt.trim(),
      schedule,
      emailDelivery: { isEnabled: emailEnabled, timing: emailTiming },
    });
  }, [name, prompt, type, hour, minute, runAt, daysOfWeek, dayOfMonth, emailEnabled, emailTiming, onSubmit]);

  const toggleDay = useCallback((day) => {
    setDaysOfWeek((current) => {
      if (!current.includes(day)) return [...current, day].sort();
      return current.length > 1 ? current.filter((value) => value !== day) : current;
    });
  }, []);

  const chipStyle = useCallback((selected) => [
    styles.chip,
    {
      borderColor: selected ? colors.text_color_secondary : border,
      backgroundColor: selected ? colors.solid_color_secondary : 'transparent',
    },
  ], [border, colors]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={isBusy ? undefined : onClose}
      accessibilityViewIsModal
    >
      <Pressable style={styles.modalOverlay} onPress={isBusy ? undefined : onClose}>
        <Pressable
          style={[
            styles.modalCard,
            isMobile && styles.modalCardMobile,
            { backgroundColor: colors.solid_color_primary, borderColor: border },
          ]}
          onPress={(event) => event.stopPropagation()}
        >
          <ScrollView
            contentContainerStyle={styles.modalContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={!isMobile}
            showsHorizontalScrollIndicator={false}
          >
            <View style={styles.modalHeader}>
              <View style={{ flex: 1 }}>
                <Text size="l" weight="bold" tag="h2">{editing ? 'Edit schedule' : 'New schedule'}</Text>
                <Text size="xs" color="secondary" style={{ marginTop: 2 }}>
                  Give Work instructions and choose when it should run.
                </Text>
              </View>
              <Button
                prependIcon="xmark"
                size="small"
                variant="transparent"
                onPress={onClose}
                isDisabled={isBusy}
                role="button"
                accessibilityRole="button"
                accessibilityLabel="Close schedule form"
              />
            </View>
            <View>
              <Text size="xs" color="secondary" weight="semibold" style={styles.fieldLabel}>Name</Text>
              <TextInput
                style={{ ...styles.input, borderColor: border, color: colors.text_color_primary }}
                value={name}
                onChangeText={setName}
                placeholder="Morning tech brief"
                placeholderTextColor={colors.text_color_tertiary}
                maxLength={200}
                accessibilityLabel="Schedule name"
                autoFocus={!isMobile && !editing && Platform.OS === 'web'}
              />
            </View>
            <View>
              <Text size="xs" color="secondary" weight="semibold" style={styles.fieldLabel}>Instructions</Text>
              <TextInput
                style={{ ...styles.input, borderColor: border, color: colors.text_color_primary, minHeight: 88, textAlignVertical: 'top' }}
                value={prompt}
                onChangeText={setPrompt}
                placeholder="Research overnight AI news and create a concise brief with sources…"
                placeholderTextColor={colors.text_color_tertiary}
                multiline
                maxLength={20000}
                accessibilityLabel="Instructions for Work"
                accessibilityHint="Work receives these instructions each time the schedule runs"
              />
              <Text size="xxs" color="tertiary" style={styles.fieldHint}>
                Work will receive these instructions each time this schedule runs.
              </Text>
            </View>
            <View>
              <Text size="xs" color="secondary" weight="semibold" style={styles.fieldLabel}>Repeats</Text>
              <View style={styles.chipGroup} accessibilityRole="radiogroup" accessibilityLabel="Schedule frequency">
                {['once', 'daily', 'weekly', 'monthly'].map((scheduleType) => {
                  const selected = type === scheduleType;
                  return (
                    <Pressable
                      key={scheduleType}
                      onPress={() => setType(scheduleType)}
                      style={chipStyle(selected)}
                      accessibilityRole="radio"
                      accessibilityState={{ checked: selected }}
                      accessibilityLabel={scheduleType === 'once' ? 'Once' : `${scheduleType} schedule`}
                    >
                      {selected && <Icon name="check" size={9} color={colors.text_color_primary} />}
                      <Text size="xxs" weight={selected ? 'semibold' : 'regular'} color={selected ? undefined : 'secondary'} style={styles.capitalize}>
                        {scheduleType}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
            {type === 'once' ? (
              <View>
                <Text size="xs" color="secondary" weight="semibold" style={styles.fieldLabel}>Date and time</Text>
                {Platform.OS === 'web' ? (
                  <WebNativeInput
                    type="datetime-local"
                    value={runAt}
                    onChange={setRunAt}
                    label="Date and time to run"
                    colors={colors}
                    border={border}
                  />
                ) : (
                  <>
                    <TextInput
                      style={{ ...styles.input, borderColor: border, color: colors.text_color_primary }}
                      value={runAt}
                      onChangeText={setRunAt}
                      placeholder="2026-09-01T09:00"
                      placeholderTextColor={colors.text_color_tertiary}
                      autoCapitalize="none"
                      accessibilityLabel="Date and time to run"
                      accessibilityHint="Enter a local date and time, for example 2026-09-01T09:00"
                    />
                    <Text size="xxs" color="tertiary" style={styles.fieldHint}>Use YYYY-MM-DDTHH:mm.</Text>
                  </>
                )}
              </View>
            ) : (
              <View>
                <Text size="xs" color="secondary" weight="semibold" style={styles.fieldLabel}>Time</Text>
                {Platform.OS === 'web' ? (
                  <WebNativeInput
                    type="time"
                    value={`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`}
                    onChange={(value) => {
                      const [nextHour, nextMinute] = String(value || '').split(':');
                      if (nextHour != null) setHour(Math.min(23, Math.max(0, Number(nextHour) || 0)));
                      if (nextMinute != null) setMinute(Math.min(59, Math.max(0, Number(nextMinute) || 0)));
                    }}
                    label="Time of day"
                    colors={colors}
                    border={border}
                  />
                ) : (
                  <View style={styles.timeFields}>
                    <View style={{ flex: 1 }}>
                      <Text size="xxs" color="tertiary" style={styles.fieldLabel}>Hour</Text>
                      <TextInput
                        style={{ ...styles.input, borderColor: border, color: colors.text_color_primary }}
                        value={String(hour)}
                        onChangeText={(value) => setHour(Math.min(23, Math.max(0, Number(value) || 0)))}
                        keyboardType="number-pad"
                        accessibilityLabel="Hour, from 0 to 23"
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text size="xxs" color="tertiary" style={styles.fieldLabel}>Minute</Text>
                      <TextInput
                        style={{ ...styles.input, borderColor: border, color: colors.text_color_primary }}
                        value={String(minute)}
                        onChangeText={(value) => setMinute(Math.min(59, Math.max(0, Number(value) || 0)))}
                        keyboardType="number-pad"
                        accessibilityLabel="Minute, from 0 to 59"
                      />
                    </View>
                  </View>
                )}
                <Text size="xxs" color="tertiary" style={styles.fieldHint}>
                  {describeWorkSchedule({ type, timeOfDay: { hour, minute }, daysOfWeek, dayOfMonth })}
                </Text>
              </View>
            )}
            {type === 'weekly' && (
              <View>
                <Text size="xs" color="secondary" weight="semibold" style={styles.fieldLabel}>Days</Text>
                <View style={styles.chipGroup} accessibilityLabel="Days of the week">
                  {DAYS.map((day, index) => {
                    const selected = daysOfWeek.includes(index);
                    return (
                      <Pressable
                        key={day}
                        onPress={() => toggleDay(index)}
                        style={chipStyle(selected)}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: selected }}
                        accessibilityLabel={moment().day(index).format('dddd')}
                      >
                        {selected && <Icon name="check" size={9} color={colors.text_color_primary} />}
                        <Text size="xxs" weight={selected ? 'semibold' : 'regular'} color={selected ? undefined : 'secondary'}>{day}</Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            )}
            {(type === 'monthly') && (
              <View>
                <Text size="xs" color="secondary" weight="semibold" style={styles.fieldLabel}>Day of month</Text>
                <TextInput
                  style={{ ...styles.input, borderColor: border, color: colors.text_color_primary }}
                  value={String(dayOfMonth)}
                  onChangeText={(value) => setDayOfMonth(Math.min(31, Math.max(1, Number(value) || 1)))}
                  keyboardType="number-pad"
                  accessibilityLabel="Day of month, from 1 to 31"
                />
              </View>
            )}
            <View style={[styles.deliveryPanel, { borderColor: border, backgroundColor: colors.solid_color_tertiary }]}>
              <View style={styles.deliveryHeader}>
                <View style={{ flex: 1, paddingRight: 12 }}>
                  <Text size="s" weight="semibold">Email delivery</Text>
                  <Text size="xxs" color="secondary" style={{ marginTop: 2 }}>Send each completed result to your inbox.</Text>
                </View>
                <Switch
                  value={emailEnabled}
                  onValueChange={setEmailEnabled}
                  accessibilityLabel="Email completed results"
                  accessibilityState={{ checked: emailEnabled }}
                />
              </View>
              {emailEnabled && (
                <View style={styles.chipGroup} accessibilityRole="radiogroup" accessibilityLabel="Email delivery timing">
                  {TIMINGS.map((timing) => {
                    const selected = emailTiming === timing.value;
                    return (
                      <Pressable
                        key={timing.value}
                        onPress={() => setEmailTiming(timing.value)}
                        style={chipStyle(selected)}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: selected }}
                      >
                        {selected && <Icon name="check" size={9} color={colors.text_color_primary} />}
                        <Text size="xxs" weight={selected ? 'semibold' : 'regular'} color={selected ? undefined : 'secondary'}>{timing.label}</Text>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            </View>
            <View style={[styles.modalActions, isMobile && styles.modalActionsMobile]}>
              <Button text="Cancel" size="small" variant="secondary" onPress={onClose} isDisabled={isBusy} role="button" accessibilityRole="button" />
              <Button
                text={editing ? 'Save changes' : 'Create schedule'}
                size="small"
                onPress={submit}
                isDisabled={isBusy || !name.trim() || !prompt.trim() || (type === 'once' && !runAt) || (type === 'weekly' && !daysOfWeek.length)}
                isLoading={isBusy}
                role="button"
                accessibilityRole="button"
              />
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

export default function CoworkerSchedulesRoute() {
  const { colors } = useTheme();
  const { isXS: isMobile, showsScrollIndicator } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const hasUser = useAuthStore((s) => !!s.user);
  const { addToast } = useToastStore((s) => s.actions);

  const schedules = useCoworkerStore(useShallow((s) => s.schedules));
  const isLoading = useCoworkerStore((s) => s.schedulesLoading);
  const error = useCoworkerStore((s) => s.schedulesError);
  const fetchSchedules = useCoworkerStore((s) => s.fetchSchedules);
  const createSchedule = useCoworkerStore((s) => s.createSchedule);
  const updateSchedule = useCoworkerStore((s) => s.updateSchedule);
  const deleteSchedule = useCoworkerStore((s) => s.deleteSchedule);
  const runScheduleNow = useCoworkerStore((s) => s.runScheduleNow);

  const [refreshing, setRefreshing] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    if (hasUser) fetchSchedules();
  }, [hasUser, fetchSchedules]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchSchedules();
    setRefreshing(false);
  }, [fetchSchedules]);

  const handleSubmit = useCallback(async (body) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      if (editing) {
        await updateSchedule(editing.id, body);
        addToast('Schedule updated', 'success');
      } else {
        await createSchedule(body);
        addToast('Schedule created', 'success');
      }
      setFormVisible(false);
      setEditing(null);
    } catch (err) {
      addToast(err?.message || 'Failed to save schedule', 'error');
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, editing, updateSchedule, createSchedule, addToast]);

  const handleToggle = useCallback(async (schedule, enabled) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await updateSchedule(schedule.id, { isEnabled: enabled });
      addToast(enabled ? 'Schedule activated' : 'Schedule paused', 'success');
    } catch (err) {
      addToast(err?.message || 'Failed to update schedule', 'error');
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, updateSchedule, addToast]);

  const handleRun = useCallback(async (schedule) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      const out = await runScheduleNow(schedule.id);
      if (out?.id) router.push(`/coworker/${out.id}`);
    } catch (err) {
      addToast(err?.message || 'Failed to run schedule', 'error');
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, runScheduleNow, addToast, router]);

  const handleDelete = useCallback(async (schedule) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await deleteSchedule(schedule.id);
      addToast('Schedule deleted', 'info');
    } catch (err) {
      addToast(err?.message || 'Failed to delete schedule', 'error');
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, deleteSchedule, addToast]);

  const confirmDelete = useCallback((schedule) => {
    const title = schedule.name?.trim() || 'this schedule';
    showConfirmationModal({
      title: `Delete “${title}”?`,
      message: 'This stops all future runs and cannot be undone.',
      confirmText: 'Delete schedule',
      isDangerous: true,
      onConfirm: () => handleDelete(schedule),
    });
  }, [handleDelete, showConfirmationModal]);

  if (!hasUser) {
    return (
      <>
        <MetaHead title="Scheduled work — Gab" />
        <View style={[styles.container, { paddingTop: insets.top + 16, paddingHorizontal: 16 }]}>
          <StatusContainer type="empty" title="Sign in to schedule work" message="Schedules run Work in the cloud on a cadence, and can email you the result." />
        </View>
      </>
    );
  }

  const border = colors.border_color_secondary;

  return (
    <>
      <MetaHead title="Scheduled work — Gab" />
      <ScrollView
        style={{ backgroundColor: colors.solid_color_block }}
        contentContainerStyle={{
          paddingTop: insets.top + 16,
          paddingBottom: insets.bottom + 24,
          paddingHorizontal: isMobile ? 14 : 20,
          maxWidth: isMobile ? '100%' : 800,
          width: '100%',
          alignSelf: 'center',
        }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={showsScrollIndicator}
      >
        <View style={styles.pageHeader}>
          <Pressable
            onPress={() => openHomeComposer(router, { work: true })}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Back to Work"
            style={({ pressed }) => [styles.backButton, pressed && styles.pressed]}
          >
            <Icon name="arrow-left" size={16} color={colors.text_color_secondary} />
          </Pressable>
          <Text size="xl" weight="bold" tag="h1">Scheduled work</Text>
          <View style={{ flex: 1 }} />
          <Button
            text={isMobile ? 'New' : 'New schedule'}
            prependIcon="plus"
            size="small"
            onPress={() => { setEditing(null); setFormVisible(true); }}
            role="button"
            accessibilityRole="button"
            accessibilityLabel="Create a new schedule"
          />
        </View>
        <Text size="s" color="secondary" style={[styles.pageSubtitle, isMobile && styles.pageSubtitleMobile]}>
          Let Work handle recurring jobs automatically and send the finished result to your inbox.
        </Text>

        {error && <StatusContainer type="error" title="Couldn't load schedules" message={error} actionText="Retry" onAction={fetchSchedules} />}
        {!error && isLoading && !schedules.length && <StatusContainer type="loading" title="Loading schedules…" />}
        {!error && !isLoading && !schedules.length && (
          <StatusContainer
            type="empty"
            title="No schedules yet"
            message="Create a schedule for a brief, report, check-in, or any job you want Work to repeat."
            actionText="Create schedule"
            onAction={() => { setEditing(null); setFormVisible(true); }}
          />
        )}
        <View style={styles.scheduleList}>
          {schedules.map((s) => (
            <ScheduleRow
              key={s.id}
              schedule={s}
              colors={colors}
              border={border}
              onEdit={(scheduleToEdit) => { setEditing(scheduleToEdit); setFormVisible(true); }}
              onRun={handleRun}
              onDelete={confirmDelete}
              onToggle={handleToggle}
              isBusy={isBusy}
            />
          ))}
        </View>
      </ScrollView>

      <ScheduleFormModal
        visible={formVisible}
        onClose={() => { setFormVisible(false); setEditing(null); }}
        onSubmit={handleSubmit}
        initial={editing}
        colors={colors}
        border={border}
        isBusy={isBusy}
        isMobile={isMobile}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-start',
    gap: 12,
  },
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  pressed: {
    opacity: 0.65,
  },
  pageSubtitle: {
    marginLeft: 40,
    marginBottom: 18,
    maxWidth: 590,
  },
  pageSubtitleMobile: {
    marginLeft: 0,
  },
  scheduleList: {
    gap: 10,
    marginTop: 12,
  },
  card: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 30,
  },
  scheduleSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 3,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
  },
  scheduleCopy: {
    flex: 1,
  },
  schedulePrompt: {
    marginTop: 10,
    lineHeight: 20,
  },
  scheduleMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 10,
  },
  cardDivider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 12,
  },
  cardActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  cardAction: {
    flexGrow: 1,
    minWidth: 80,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 18,
    width: '100%',
    maxWidth: 560,
    maxHeight: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 18px 60px rgba(0,0,0,0.28)',
      },
    }),
  },
  modalCardMobile: {
    padding: 14,
    maxHeight: '94%',
  },
  modalContent: {
    gap: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  fieldLabel: {
    marginBottom: 6,
  },
  fieldHint: {
    marginTop: 5,
    lineHeight: 16,
  },
  input: {
    borderWidth: 1,
    borderRadius: 10,
    minHeight: 42,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  timeFields: {
    flexDirection: 'row',
    gap: 8,
  },
  chipGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 999,
    minHeight: 32,
    paddingHorizontal: 11,
    paddingVertical: 6,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  deliveryPanel: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    gap: 12,
  },
  deliveryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 2,
  },
  modalActionsMobile: {
    marginTop: 0,
  },
});
